import {REST} from '../../api'
import type {AxiosRequestConfig} from 'axios'
import type {
    AdditionalEmailConfig,
    NotificationSettings,
    UserNotificationSettings,
    Warning,
    WarningQueryRequest,
    WarningResponse,
    WarningsConfig,
    WarningScoreApiResult,
    WarningScoreApiResultWithErrors,
    WarningScoreRequest,
    WarningScoreResponse,
    WarningsCountByLocation,
    ThreatScorePoint,
    ThreatScorePeriod,
    FleetThreatScoreSeries,
    LocationThreatScoreSeries,
    LatestThreatScoresByLocation,
    LocationThreatScoresByTagsRequest,
    ThreatScoresByTag,
} from './types/warning-and-scoring.types'
import type {GuidType} from '@/values/generic-type-definitions'
/*
 * Note: 'WarningQueryRequest' is imported using a type-only import as required by 'verbatimModuleSyntax'.
 */
const WARNING_AND_SCORING_BASE_URL = '/was/api/v1'
const WARNING_SCORES_URL = '/was/api/v1/warnings/scores'
const WARNINGS_URL = '/was/api/v1/warnings/find'

const WARNINGS_CONFIG_URL = '/was/api/v1/warnings/config/warningsConfig'
const FIND_WARNING_ENDPOINT = '/was/api/v1/warnings'
const NUMBER_OF_WARNINGS_BY_LOCATION = '/was/api/v1/warnings/count/byLocation?states=active'

// ------------------ notification ---------------------------------
const NOTIFICATIONS_RESOURCE = `${WARNING_AND_SCORING_BASE_URL}/notifications`
const PREDEFINED_SETTINGS_URL = `${NOTIFICATIONS_RESOURCE}/settings/predefined`

// ------------------ settings --------------------------------------------
const USER_NOTIFICATION_SETTINGS_RESOURCE = `${NOTIFICATIONS_RESOURCE}/users`
const ASSIGN_USER_NOTIFICATION_SETTINGS_URL = `${USER_NOTIFICATION_SETTINGS_RESOURCE}/assign`
const UNASSIGN_USER_NOTIFICATION_SETTINGS_URL = `${USER_NOTIFICATION_SETTINGS_RESOURCE}/unassign`

// ------------------ report --------------------------------------------
const REPORT_URL = `${WARNING_AND_SCORING_BASE_URL}/reports`

// ------------------ threat scores (CDS-6008) ----------------------------
const SCORES_RESOURCE = `${WARNING_AND_SCORING_BASE_URL}/scores`
const LATEST_FLEET_THREAT_SCORE_URL = `${SCORES_RESOURCE}/latestFleetThreatScore`
const FLEET_THREAT_SCORE_URL = `${SCORES_RESOURCE}/fleetThreatScore`
const LATEST_LOCATION_THREAT_SCORES_URL = `${SCORES_RESOURCE}/latestLocationThreatScores`
// Note the capital L. This is how the route is registered in
// go-warning-and-scoring-api/pkg/api/route_scores.go:232.
const LOCATION_THREAT_SCORE_URL = `${SCORES_RESOURCE}/LocationThreatScore`
const LOCATION_TAG_THREAT_SCORE_URL = `${SCORES_RESOURCE}/locationTagThreatScore`
const LOCATION_THREAT_SCORES_BY_TAGS_URL = `${SCORES_RESOURCE}/locationThreatScoresByTags`

export async function fetchWarningScores(
    body: WarningScoreRequest,
    config?: AxiosRequestConfig,
): Promise<WarningScoreResponse> {
    try {
        const res = await REST.post<WarningScoreResponse>(WARNING_SCORES_URL, body, config)
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch warning scores: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function fetchWarnings(
    body: WarningQueryRequest,
    config?: AxiosRequestConfig,
): Promise<WarningResponse> {
    const res = await REST.post<WarningResponse>(WARNINGS_URL, body, config)
    return res.data
}

export async function fetchWarningsConfig(config?: AxiosRequestConfig): Promise<WarningsConfig> {
    try {
        const res = await REST.get<WarningsConfig>(WARNINGS_CONFIG_URL, config)

        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch warnings config: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function fetchWarningById(warningId: string, body: WarningQueryRequest) {
    try {
        const response = await REST.post<Warning>(
            `${FIND_WARNING_ENDPOINT}/${warningId}/find`,
            body,
        )
        return response.data
    } catch (error) {
        throw new Error(
            `Failed to fetch warning ${warningId}: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function fetchUserNotificationSettings(userId: GuidType) {
    try {
        const response = await REST.get<UserNotificationSettings | null>(
            `${USER_NOTIFICATION_SETTINGS_RESOURCE}/${userId}`,
        )
        return response.data
    } catch (error) {
        throw new Error(
            `Failed to fetch current user notification settings: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function fetchPredefinedNotificationSettings() {
    try {
        const response = await REST.get<NotificationSettings[]>(PREDEFINED_SETTINGS_URL)
        return response.data
    } catch (error) {
        throw new Error(
            `Failed to fetch current user notification settings: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export interface AssignUserNotificationSettingsParams {
    userId: GuidType
    notificationSettingId: GuidType
    additionalEmailConfig: AdditionalEmailConfig
}

export interface UnassignUserNotificationSettingsParams {
    userId: GuidType
    notificationSettingId: GuidType
}

export async function assignUserNotificationSettings(
    {additionalEmailConfig, notificationSettingId, userId}: AssignUserNotificationSettingsParams,
    config?: AxiosRequestConfig,
) {
    try {
        const response = await REST.put<WarningScoreApiResultWithErrors>(
            ASSIGN_USER_NOTIFICATION_SETTINGS_URL + `/${userId}`,
            {
                notificationSettingId,
                ...additionalEmailConfig,
            },
            {
                ...config,
            },
        )
        return response.data
    } catch (error) {
        throw new Error(
            `Failed to assign user notification settings: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function unassignUserNotificationSettings(
    {notificationSettingId, userId}: UnassignUserNotificationSettingsParams,
    config?: AxiosRequestConfig,
) {
    try {
        const response = await REST.delete<WarningScoreApiResult>(
            UNASSIGN_USER_NOTIFICATION_SETTINGS_URL + `/${userId}/${notificationSettingId}`,
            {...config},
        )
        return response.data
    } catch (error) {
        throw new Error(
            `Failed to unassign user notification settings: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function fetchWarningsCountByLocation(
    config?: AxiosRequestConfig,
): Promise<WarningsCountByLocation> {
    try {
        const res = await REST.get<WarningsCountByLocation>(NUMBER_OF_WARNINGS_BY_LOCATION, config)

        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch warnings config: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export function createEventsLogReport(body: unknown) {
    return REST.post(REPORT_URL, body)
}

// ------------------ threat scores (CDS-6008) ----------------------------
// Direct replacements for the ETS WebSocket queries. Previously the UI sent
// `query-new/*` frames to basal-ganglia, which read InfluxDB. These call
// warning-and-scoring instead.

export async function fetchLatestFleetThreatScore(
    config?: AxiosRequestConfig,
): Promise<ThreatScorePoint> {
    try {
        const res = await REST.get<ThreatScorePoint>(LATEST_FLEET_THREAT_SCORE_URL, config)
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch latest fleet threat score: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function fetchFleetThreatScore(
    timePeriod: ThreatScorePeriod,
    config?: AxiosRequestConfig,
): Promise<FleetThreatScoreSeries> {
    try {
        const res = await REST.get<FleetThreatScoreSeries>(FLEET_THREAT_SCORE_URL, {
            ...config,
            params: {timePeriod},
        })
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch fleet threat score for ${timePeriod}: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function fetchLatestLocationThreatScores(
    config?: AxiosRequestConfig,
): Promise<LatestThreatScoresByLocation> {
    try {
        const res = await REST.get<LatestThreatScoresByLocation>(
            LATEST_LOCATION_THREAT_SCORES_URL,
            config,
        )
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch latest location threat scores: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function fetchLocationThreatScore(
    timePeriod: ThreatScorePeriod,
    location: GuidType,
    config?: AxiosRequestConfig,
): Promise<LocationThreatScoreSeries> {
    try {
        const res = await REST.get<LocationThreatScoreSeries>(LOCATION_THREAT_SCORE_URL, {
            ...config,
            params: {timePeriod, location},
        })
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch location threat score for ${location}: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

export async function fetchLocationTagThreatScore(
    timePeriod: ThreatScorePeriod,
    tag: string,
    config?: AxiosRequestConfig,
): Promise<LocationThreatScoreSeries> {
    try {
        const res = await REST.get<LocationThreatScoreSeries>(LOCATION_TAG_THREAT_SCORE_URL, {
            ...config,
            params: {timePeriod, tag},
        })
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch location tag threat score for ${tag}: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}

/**
 * Bulk scores for a set of location tags. Returns every score point in the
 * period per tag; the caller reduces them to a max and an average.
 *
 * Pass a non-empty `tags`. An empty array returns nothing, because the
 * server's fallback to "every tag" only triggers on a nil field and this body
 * always writes the key. See LocationThreatScoresByTagsRequest.
 */
export async function fetchLocationThreatScoresByTags(
    timePeriod: ThreatScorePeriod,
    tags: GuidType[],
    config?: AxiosRequestConfig,
): Promise<ThreatScoresByTag> {
    try {
        const body: LocationThreatScoresByTagsRequest = {tags}
        const res = await REST.post<ThreatScoresByTag>(LOCATION_THREAT_SCORES_BY_TAGS_URL, body, {
            ...config,
            params: {timePeriod},
        })
        return res.data
    } catch (error) {
        throw new Error(
            `Failed to fetch location threat scores by tags: ${error instanceof Error ? error.message : String(error)}`,
        )
    }
}
